import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Button from '../components/common/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Input from '../components/common/Input';
import FileUploadButton from '../components/common/FileUploadButton';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import ImageZoomDialog from '../components/common/ImageZoomDialog';
import useQuestionDetailState from '../hooks/useQuestionDetailState';
import useOptionConfig from '../hooks/useOptionConfig';
import { usePrivileges } from '../config/privilegeConstant';

function QuestionDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const question = location.state?.question;
  const { hasPrivilege, codes } = usePrivileges();

  const {
    imageUrls,
    htmlUrls,
    assetsLoading,
    assetsError,
    questionText,
    setQuestionText,
    answerText,
    setAnswerText,
    isActive,
    monthYearInput,
    setMonthYearInput,
    handleFileChange,
    handleSave,
    loadAssets,
    handleImageDelete,
    handleRemoveNewImage,
    newImages,
    saveLoading,
    saveError,
    newHtmlFile,
    handleHtmlFileChange,
    handleRemoveNewHtml,
    handleHtmlDelete,
    toggleActiveAndSave,
    showHtmlModal,
    setShowHtmlModal,
    htmlViewContent,
    handleHtmlPreview,
    questionId,
    zoomImage,
    setZoomImage
  } = useQuestionDetailState(question);

  const handleDownloadHtml = (htmlFile) => {
    const a = document.createElement('a');
    a.href = htmlFile.url;
    a.download = htmlFile.fileName;
    a.click();
  };

  const { config } = useOptionConfig();
  const labels = config?.labels?.questionDetail || {};

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  if (!question && !questionId) {
    return (
      <Layout>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No question data found.</Typography>
          <Button variant="contained" onClick={() => navigate(-1)}>Go Back</Button>
        </Paper>
      </Layout>
    );
  }

  const displayId = question?.id || questionId;

  return (
    <Layout>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">{labels.pageTitle || "Edit Question"}</Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>ID: {displayId ?? 'N/A'}</Typography>
        </Box>
        <Input
          label={labels.lblQuestion || "Question"}
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          multiline
          minRows={3}
        />
        <Input
          label={labels.lblAnswer || "Answer"}
          value={answerText}
          onChange={(e) => setAnswerText(e.target.value)}
          multiline
          minRows={4}
        />
        <Input
          label={labels.lblMonthYear || "Month Year"}
          value={monthYearInput}
          onChange={(e) => setMonthYearInput(e.target.value)}
          placeholder={labels.placeholderMonthYear || "e.g., November 2023, May 2025"}
          fullWidth
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>{labels.uploadImages || "Images"}</Typography>
          <Grid container spacing={2}>
            {imageUrls.map((img, idx) => {
              const { fileName, url } = img;
              return (
                <Grid item xs={6} sm={4} md={3} key={fileName || idx}>
                  <Paper variant="outlined" sx={{ p: 1, position: 'relative', textAlign: 'center' }}>
                    <Box
                      component="img"
                      src={url}
                      alt={`Existing ${idx}`}
                      sx={{ width: '100%', height: 150, objectFit: 'contain', mb: 1, cursor: 'pointer' }}
                      onClick={() => setZoomImage(url)}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleImageDelete(fileName)}
                      fullWidth
                    >
                      {labels.btnDelete || "Delete"}
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
            {newImages.map((file, idx) => (
              <Grid item xs={6} sm={4} md={3} key={`new-${idx}`}>
                <Paper variant="outlined" sx={{ p: 1, position: 'relative', textAlign: 'center' }}>
                  <Box
                    component="img"
                    src={URL.createObjectURL(file)}
                    alt={`New ${idx}`}
                    sx={{ width: '100%', height: 150, objectFit: 'contain', mb: 1, cursor: 'pointer' }}
                    onClick={() => setZoomImage(URL.createObjectURL(file))}
                  />
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={() => handleRemoveNewImage(idx)}
                    fullWidth
                  >
                    {labels.btnRemove || "Remove"}
                  </Button>
                </Paper>
              </Grid>
            ))}
            <Grid item xs={6} sm={4} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  height: '100%',
                  minHeight: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderStyle: 'dashed'
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <FileUploadButton
                    title={labels.uploadImages || "Add Image"}
                    description="Click/Drag"
                    acceptedFileTypes="image/*"
                    onFileUpload={(file) => handleFileChange({ target: { files: [file] } })}
                    fileName=""
                    fullWidth
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" color="primary" gutterBottom>HTML</Typography>
        {assetsLoading && <CircularProgress />}
        {assetsError && <Typography color="error">{assetsError}</Typography>}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', mt: 2 }}>
          <Box sx={{ flex: 1, width: '100%', mr: { md: 4 } }}>
            <Grid container spacing={2}>
              {htmlUrls.map((htmlFile, idx) => (
                <Grid item xs={12} sm={6} md={6} key={htmlFile.fileName || idx}>
                  <Box sx={{ border: (theme) => `1px solid ${theme.palette.primary.main}`, bgcolor: (theme) => theme.palette.action.hover, borderRadius: 2, p: 2, position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{htmlFile.fileName}</Typography>
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" size="small" onClick={() => handleHtmlPreview(htmlFile)}>
                        {labels.btnPreview || "Preview"}
                      </Button>
                      <Button variant="outlined" size="small" onClick={() => handleDownloadHtml(htmlFile)}>
                        {labels.btnDownload || "Download"}
                      </Button>
                      <Button variant="outlined" color="error" size="small" onClick={() => handleHtmlDelete(htmlFile.fileName)}>
                        {labels.btnDelete || "Delete"}
                      </Button>
                    </Stack>
                  </Box>
                </Grid>
              ))}
              {newHtmlFile && (
                <Grid item xs={12} sm={6} md={6}>
                  <Box sx={{ border: (theme) => `1px solid ${theme.palette.success.main}`, bgcolor: 'success.light', bgOpacity: 0.1, borderRadius: 2, p: 2, position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{newHtmlFile.name} {labels.suffixNew || "(New)"}</Typography>
                    <Button variant="outlined" color="warning" size="small" onClick={handleRemoveNewHtml}>
                      {labels.btnRemove || "Remove"}
                    </Button>
                  </Box>
                </Grid>
              )}
              {htmlUrls.length === 0 && !newHtmlFile && (
                <Grid item xs={12} sm={6} md={6}>
                  <FileUploadButton
                    title={labels.uploadHtmlTitle || "Upload HTML"}
                    description={labels.uploadHtmlDesc || "Click/Drag .html file"}
                    acceptedFileTypes=".html"
                    onFileUpload={handleHtmlFileChange}
                    fileName=""
                    fullWidth
                    sx={{ height: '100%', minHeight: 120, border: '2px dashed', borderColor: 'primary.main' }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <Box sx={{ mt: { xs: 2, md: 0 }, minWidth: 'fit-content', display: 'flex', alignItems: 'flex-end', height: '100%', pt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              {saveError && <Typography color="error">{saveError}</Typography>}
              <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
                {labels.btnCancel || "Cancel"}
              </Button>
              {hasPrivilege(codes.DELETE_QB_QUESTIONS) && (
                <Button
                  variant={isActive ? "contained" : "outlined"}
                  color={isActive ? "error" : "success"}
                  onClick={toggleActiveAndSave}
                >
                  {isActive ? (labels.btnDeactivate || "Deactivate") : (labels.btnActivate || "Activate")}
                </Button>
              )}
              {hasPrivilege(codes.EDIT_QB_QUESTIONS) && (
                <Button variant="contained" onClick={() => handleSave()} disabled={saveLoading}>
                  {saveLoading ? (labels.btnSaving || 'Saving...') : (labels.btnSave || 'Save Changes')}
                </Button>
              )}
            </Stack>
          </Box>
        </Box>

        <Dialog
          open={showHtmlModal}
          onClose={() => setShowHtmlModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {labels.dialogHtmlPreview || "HTML Preview"}
            <IconButton
              aria-label="close"
              onClick={() => setShowHtmlModal(false)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              dangerouslySetInnerHTML={{ __html: htmlViewContent }}
              sx={{
                width: '100%',
                minHeight: '60vh',
                '& img': { maxWidth: '100%', height: 'auto' }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowHtmlModal(false)}>{labels.btnClose || "Close"}</Button>
          </DialogActions>
        </Dialog>
        <ImageZoomDialog
          open={!!zoomImage}
          imageUrl={zoomImage}
          onClose={() => setZoomImage(null)}
        />
      </Paper>
    </Layout>
  );
}

export default QuestionDetailPage;
